import { useMemo } from 'react';
import { Cell, Label, Pie, PieChart } from 'recharts';

import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from '@/components/ui/card';
import { ChartContainer, ChartTooltip, ChartTooltipContent } from '@/components/ui/chart';
import { Skeleton } from '@/components/ui/skeleton';
import { useJobStats } from '@/hooks/useJobs';
import { JOB_SOURCES, type JobSourceKey } from '@/lib/jobSource';

// A stable colour per source, assigned in the JOB_SOURCES declaration order so
// the legend and pie stay in sync with the platform list. Recharts reads these
// CSS custom properties from the chart theme.
const SOURCE_COLORS: Record<JobSourceKey, string> = {
  linkedin: 'var(--chart-1)',
  indeed: 'var(--chart-2)',
  ziprecruiter: 'var(--chart-3)',
  bayt: 'var(--chart-4)',
  naukri: 'var(--chart-5)',
  manual: 'var(--muted-foreground)',
};

// Resolve the backend's source prefix (e.g. 'li', or 'manual') to its display
// meta. The stats query already collapses null sources to the 'manual' bucket.
const META_BY_PREFIX = new Map(JOB_SOURCES.map((s) => [s.prefix ?? 'manual', s]));

const chartConfig = {
  jobs: { label: 'Jobs' },
};

export function JobsBySourceChart() {
  const { data, isLoading, isError } = useJobStats();

  const sources = useMemo(() => {
    if (!data?.bySource) return [];
    return data.bySource
      .map(({ source, count }) => {
        // `source` is a prefix ('li') or 'manual'; fall back to the raw value's
        // key mapping, else bucket anything unrecognised under Manual.
        const meta = META_BY_PREFIX.get(source) ?? META_BY_PREFIX.get('manual')!;
        // Coerce defensively: a missing/NaN count would poison `total` and, via
        // the `total === 0` guard below, silently render a broken chart.
        const jobs = Number(count) || 0;
        return { key: meta.key, source: meta.label, jobs, color: SOURCE_COLORS[meta.key] };
      })
      // Fold any prefixes that collapse to the same platform (e.g. unknown →
      // manual) into a single slice, then re-sort largest-first (folding can
      // change the running order).
      .reduce<{ key: JobSourceKey; source: string; jobs: number; color: string }[]>((acc, cur) => {
        const existing = acc.find((s) => s.key === cur.key);
        if (existing) existing.jobs += cur.jobs;
        else acc.push(cur);
        return acc;
      }, [])
      .sort((a, b) => b.jobs - a.jobs);
  }, [data]);

  const total = sources.reduce((sum, s) => sum + s.jobs, 0);

  return (
    <Card>
      <CardHeader>
        <CardTitle>Jobs by Source</CardTitle>
        <CardDescription>Distribution across scraping origins</CardDescription>
      </CardHeader>
      <CardContent>
        {isLoading ? (
          <div className="flex flex-col items-center gap-4">
            <Skeleton className="size-[220px] rounded-full" />
            <div className="grid w-full grid-cols-2 gap-2">
              {Array.from({ length: 4 }).map((_, i) => (
                <Skeleton key={i} className="h-5 w-full" />
              ))}
            </div>
          </div>
        ) : isError ? (
          <p className="py-16 text-center text-sm text-muted-foreground">
            Couldn’t load source breakdown.
          </p>
        ) : total === 0 ? (
          <p className="py-16 text-center text-sm text-muted-foreground">No jobs yet.</p>
        ) : (
          <>
            <ChartContainer config={chartConfig} className="mx-auto aspect-square h-[220px]">
              <PieChart>
                <ChartTooltip content={<ChartTooltipContent nameKey="source" hideLabel />} />
                <Pie data={sources} dataKey="jobs" nameKey="source" innerRadius={58} strokeWidth={2}>
                  {sources.map((s) => (
                    <Cell key={s.key} fill={s.color} stroke="var(--card)" />
                  ))}
                  <Label
                    content={({ viewBox }) => {
                      if (!viewBox || !('cx' in viewBox)) return null;
                      return (
                        <text x={viewBox.cx} y={viewBox.cy} textAnchor="middle" dominantBaseline="middle">
                          <tspan
                            x={viewBox.cx}
                            y={viewBox.cy}
                            className="fill-foreground text-2xl font-semibold"
                          >
                            {total.toLocaleString()}
                          </tspan>
                          <tspan
                            x={viewBox.cx}
                            y={(viewBox.cy ?? 0) + 20}
                            className="fill-muted-foreground text-xs"
                          >
                            Total jobs
                          </tspan>
                        </text>
                      );
                    }}
                  />
                </Pie>
              </PieChart>
            </ChartContainer>
            <ul className="mt-4 grid grid-cols-2 gap-2 text-sm">
              {sources.map((s) => (
                <li key={s.key} className="flex items-center gap-2">
                  <span className="size-2.5 rounded-full" style={{ backgroundColor: s.color }} />
                  <span className="text-muted-foreground">{s.source}</span>
                  <span className="ml-auto font-medium tabular-nums">{s.jobs.toLocaleString()}</span>
                </li>
              ))}
            </ul>
          </>
        )}
      </CardContent>
    </Card>
  );
}
